<template>
  <MobilePage>
    <div class="bg-white px-4 pt-4 pb-3 border-b border-gray-200">
      <button class="flex items-center -ml-1 text-accent-primary text-sm" @click="goBack">
        <ChevronLeftIcon class="h-6 w-6" />
        <span>Contacts</span>
      </button>

      <div v-if="contact" class="mt-2 space-y-1 text-sm">
        <h1 class="text-2xl font-semibold text-dark-primary">{{ fullName || '--' }}</h1>
        <div v-for="item in headerItems" :key="item.label" class="flex items-center gap-2">
          <span class="w-16 shrink-0 text-gray-500">{{ item.label }}</span>
          <a v-if="item.value" :href="item.href" :target="item.external ? '_blank' : null" rel="noopener" class="truncate text-accent-primary">{{ item.value }}</a>
          <span v-else>--</span>
        </div>
      </div>
    </div>

    <div class="p-4 space-y-4">
      <div v-if="!contact" class="text-center text-gray-500 py-8">Couldn't load contact</div>

      <template v-else>
        <div v-if="contact.personal_notes" class="bg-white rounded-lg shadow p-4 text-sm">
          <div class="text-base font-semibold text-dark-primary mb-1">Personal Notes</div>
          <MobileClampedText :text="contact.personal_notes" />
        </div>

        <section v-if="companyRelationships.length" class="space-y-2">
          <h2 class="text-lg font-semibold text-dark-primary">Company Relationships</h2>
          <MobileRelationshipCard
            v-for="relationship in companyRelationships"
            :key="relationship.id"
            :name="relationship.company && relationship.company.company_name"
            :relationship="relationship"
            collapsible
            :rows="companyRows(relationship.company || {})"
            :notes="companyNotes(relationship.company || {})"
          />
        </section>

        <section v-if="clientRelationships.length" class="space-y-2">
          <h2 class="text-lg font-semibold text-dark-primary">Client Relationships</h2>
          <MobileRelationshipCard
            v-for="relationship in clientRelationships"
            :key="relationship.id"
            :name="relationship.client && relationship.client.client_name"
            :relationship="relationship"
            :rows-after-phone="clientRows(relationship.client || {})"
          />
        </section>

        <section v-if="users.length" class="space-y-2">
          <h2 class="text-lg font-semibold text-dark-primary">User Relationships</h2>
          <div class="flex flex-wrap gap-2">
            <span v-for="user in users" :key="user.id" class="px-3 py-1 rounded-full bg-white shadow text-sm text-dark-primary">
              {{ [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username }}
            </span>
          </div>
        </section>
      </template>
    </div>
  </MobilePage>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useRouter } from 'vue-router';
import { ChevronLeftIcon } from '@heroicons/vue/20/solid';
import { linkedinUrl } from '@/components/mobile/contact/contactDisplay';
import MobilePage from '@/components/mobile/base/MobilePage.vue';
import MobileClampedText from '@/components/mobile/base/MobileClampedText.vue';
import MobileRelationshipCard from '@/components/mobile/contact/MobileRelationshipCard.vue';

const props = defineProps({
  contactId: String
})

const router = useRouter()

const state = window.history.state || {}
const contact = state.contact && state.contact.id == props.contactId ? state.contact : null

const fullName = computed(() => contact && [contact.first_name, contact.last_name].filter(Boolean).join(' '))

const headerItems = computed(() => [
  { label: 'Email', value: contact.personal_email, href: `mailto:${contact.personal_email}` },
  { label: 'LinkedIn', value: contact.linkedin_profile, href: contact.linkedin_profile && linkedinUrl(contact.linkedin_profile), external: true },
  { label: 'Phone', value: contact.personal_phone, href: `tel:${contact.personal_phone}` },
])

const companyRelationships = computed(() =>
  [...(contact.company_relationships || [])].sort((a, b) => b.is_primary_contact - a.is_primary_contact)
)
const clientRelationships = computed(() => (contact.clients || []).filter(relationship => relationship.status !== 'inactive'))
const users = computed(() => contact.users || [])

function companyRows(company) {
  return [
    { label: 'Website', value: company.website_url, href: company.website_url && linkedinUrl(company.website_url), external: true },
    { label: 'Address', value: [company.city, company.state, company.country].filter(Boolean).join(', ') },
    { label: 'Parent', value: company.parent_company, hideIfEmpty: true },
    { label: 'Investors', value: company.investors, hideIfEmpty: true },
    { label: 'Campaign', value: company.campaign && company.campaign.campaign_name },
  ]
}

function companyNotes(company) {
  return [
    { label: 'Research Notes', value: company.account_manager_research_notes },
    { label: 'Outreach Notes', value: company.out_reach_notes },
  ]
}

function clientRows(client) {
  return [
    { label: 'Address', value: [client.address, client.city, client.state].filter(Boolean).join(', ') },
    { label: 'Time Zone', value: client.time_zone, hideIfEmpty: true },
  ]
}

function goBack() {
  if (window.history.state && window.history.state.back === '/pwa/contacts') {
    router.back()
  } else {
    router.push({ name: 'pwa-contacts' })
  }
}
</script>
